import React, { useState } from "react";
import { Upload, ShieldCheck, ShieldX, Download, AlertTriangle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Card } from "../ui/card";
import { Button } from "../ui/button";
import { Checkbox } from "../ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../ui/dialog";
import { Field, OLIVE, errMsg, fmtDateTime, downloadFile } from "./dpaUtils";

const KINDS = [["signed_agreement", "Signed agreement"], ["signature_evidence", "Signature evidence"], ["amendment", "Agreed amendment"], ["attachment", "Supporting attachment"]];
const CHECKS = [["client_signature_present", "Client's authorised signature is present"], ["provider_signature_present", "Castlewood's authorised signature is present"],
  ["matches_issued_version", "The signed copy matches the issued version (reference, version and wording)"], ["signatories_authorised", "Both signatories are authorised to sign"]];

const UploadBox = ({ a, onDone }) => {
  const issued = a.state === "issued";
  const [kind, setKind] = useState(issued ? "signed_agreement" : "attachment");
  const [desc, setDesc] = useState("");
  const [busy, setBusy] = useState(false);
  const upload = async (file) => {
    if (file.size > 20 * 1024 * 1024) return toast.error("Files must be 20 MB or smaller");
    const fd = new FormData(); fd.append("file", file); fd.append("kind", kind); fd.append("description", desc);
    setBusy(true);
    try { onDone((await api.post(`/dpa/agreements/${a.id}/documents`, fd)).data); setDesc(""); toast.success("Uploaded and checksummed", { description: file.name }); }
    catch (e) { toast.error("Upload failed", { description: errMsg(e) }); }
    finally { setBusy(false); }
  };
  return (
    <div className="grid md:grid-cols-[220px_1fr_auto] gap-3 items-end">
      <div className="space-y-1"><div className="text-xs font-medium">Document type</div>
        <Select value={kind} onValueChange={setKind}>
          <SelectTrigger className="rounded-none" data-testid="upload-kind-select"><SelectValue /></SelectTrigger>
          <SelectContent>{KINDS.filter(([k]) => issued || !["signed_agreement", "signature_evidence"].includes(k)).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
        </Select></div>
      <Field label="Description (optional)" value={desc} onChange={setDesc} testId="upload-description" />
      <label className={`text-sm cursor-pointer inline-flex items-center gap-1 border px-3 h-9 hover:bg-slate-50 ${busy ? "opacity-50 pointer-events-none" : ""}`} data-testid="upload-document-btn">
        <Upload className="h-4 w-4" />{busy ? "Uploading…" : "Upload PDF / PNG / JPG"}
        <input type="file" accept="application/pdf,image/png,image/jpeg" className="hidden" data-testid="upload-document-input" onChange={(e) => { e.target.files[0] && upload(e.target.files[0]); e.target.value = ""; }} />
      </label>
    </div>
  );
};

const VerifyDialog = ({ a, open, setOpen, onDone }) => {
  const { user } = useAuth();
  const signed = a.documents.filter((d) => d.kind === "signed_agreement");
  const [f, setF] = useState({ document_id: signed[0]?.id || "", verifier_name: user?.name || "", notes: "" });
  const [checks, setChecks] = useState({});
  const doc = signed.find((d) => d.id === f.document_id);
  const same = doc && doc.created_by === user?.email;
  const submit = async () => {
    try { onDone((await api.post(`/dpa/agreements/${a.id}/verify`, { ...f, ...Object.fromEntries(CHECKS.map(([k]) => [k, !!checks[k]])) })).data); setOpen(false); toast.success("Signatures verified"); }
    catch (e) { toast.error("Not verified", { description: errMsg(e) }); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="rounded-none max-w-xl" data-testid="verify-dialog">
        <DialogHeader><DialogTitle>Verify signed agreement</DialogTitle>
          <DialogDescription>Check the uploaded signed copy yourself. Verification is recorded against the file's SHA-256 checksum.</DialogDescription></DialogHeader>
        <div className="space-y-1"><div className="text-xs font-medium">Signed copy being verified</div>
          <Select value={f.document_id} onValueChange={(v) => setF({ ...f, document_id: v })}>
            <SelectTrigger className="rounded-none" data-testid="verify-document-select"><SelectValue placeholder="Choose signed copy" /></SelectTrigger>
            <SelectContent>{signed.map((d) => <SelectItem key={d.id} value={d.id}>{d.filename} · {d.sha256.slice(0, 10)}…</SelectItem>)}</SelectContent>
          </Select></div>
        {same && <div className="text-xs text-amber-900 bg-amber-50 border border-amber-200 p-2 flex gap-1" data-testid="verify-same-person-warning"><AlertTriangle className="h-4 w-4 shrink-0" />You uploaded this file yourself. Independent verification by another Super Admin is better practice — this will be noted on the record.</div>}
        {CHECKS.map(([k, l]) => (
          <label key={k} className="flex items-start gap-2 text-sm"><Checkbox checked={!!checks[k]} onCheckedChange={(v) => setChecks({ ...checks, [k]: !!v })} data-testid={`verify-check-${k}`} />{l}</label>
        ))}
        <Field label="Verifier name" required value={f.verifier_name} onChange={(v) => setF({ ...f, verifier_name: v })} testId="verify-name-input" />
        <Field label="Notes" multiline value={f.notes} onChange={(v) => setF({ ...f, notes: v })} testId="verify-notes-input" />
        <DialogFooter><Button className="rounded-none text-white" style={OLIVE} disabled={!f.document_id || !f.verifier_name.trim() || CHECKS.some(([k]) => !checks[k])} onClick={submit} data-testid="verify-confirm-btn">Confirm verification</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const RejectDialog = ({ a, open, setOpen, onDone }) => {
  const [reason, setReason] = useState("");
  const signed = a.documents.filter((d) => d.kind === "signed_agreement");
  const [docId, setDocId] = useState(signed[0]?.id || "");
  const submit = async () => {
    try { onDone((await api.post(`/dpa/agreements/${a.id}/verification/reject`, { reason, document_id: docId || null })).data); setOpen(false); setReason(""); toast.success("Evidence rejected"); }
    catch (e) { toast.error("Failed", { description: errMsg(e) }); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="rounded-none" data-testid="reject-dialog">
        <DialogHeader><DialogTitle>Reject signature evidence</DialogTitle></DialogHeader>
        <div className="space-y-1"><div className="text-xs font-medium">Signed copy being rejected</div>
          <Select value={docId} onValueChange={setDocId}>
            <SelectTrigger className="rounded-none" data-testid="reject-document-select"><SelectValue placeholder="Choose signed copy" /></SelectTrigger>
            <SelectContent>{signed.map((d) => <SelectItem key={d.id} value={d.id}>{d.filename} · {d.sha256.slice(0, 10)}…</SelectItem>)}</SelectContent>
          </Select></div>
        <Field label="Reason" required multiline value={reason} onChange={setReason} testId="reject-reason-input" />
        <DialogFooter><Button className="rounded-none bg-red-700 text-white" disabled={!reason.trim() || !docId} onClick={submit} data-testid="reject-confirm-btn">Reject evidence</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const VerificationState = ({ v }) => {
  if (!v) return <div className="text-sm text-muted-foreground" data-testid="verification-status">Not verified — recorded signature dates alone are not proof of execution.</div>;
  if (v.status === "rejected") return <div className="text-sm text-red-800 flex gap-1" data-testid="verification-status"><ShieldX className="h-4 w-4" />Evidence rejected {fmtDateTime(v.verified_at)} by {v.verified_by}: {v.reason}</div>;
  return (
    <div className="text-sm text-emerald-800 space-y-0.5" data-testid="verification-status">
      <div className="flex gap-1 font-semibold"><CheckCircle2 className="h-4 w-4" />Verified by {v.verifier_name} ({v.verified_by}) on {fmtDateTime(v.verified_at)}</div>
      <div className="text-xs">File {v.document_filename} · SHA-256 <span className="font-mono">{v.document_sha256?.slice(0, 16)}…</span>{v.notes && ` · ${v.notes}`}</div>
      {v.same_person_as_uploader && <div className="text-xs text-amber-800">Note: verifier also uploaded the file.</div>}
    </div>
  );
};

export const SigningPanel = ({ a, onChange }) => {
  const [verifying, setVerifying] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const live = a.state === "issued" && !["terminated", "superseded"].includes(a.status);
  const hasSigned = a.documents.some((d) => d.kind === "signed_agreement");
  return (
    <Card className="rounded-none p-6 space-y-4" data-testid="signing-panel">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Signed copies, evidence & verification</div>
        {live && (
          <div className="flex gap-2">
            <Button variant="outline" className="rounded-none text-red-700" disabled={!hasSigned} onClick={() => setRejecting(true)} data-testid="reject-evidence-btn"><ShieldX className="h-4 w-4 mr-1" />Reject evidence</Button>
            <Button className="rounded-none text-white" style={OLIVE} disabled={!hasSigned} onClick={() => setVerifying(true)} data-testid="verify-signatures-btn"><ShieldCheck className="h-4 w-4 mr-1" />{a.verification?.status === "verified" ? "Re-verify (replaces current)" : "Verify signatures"}</Button>
          </div>
        )}
      </div>
      <VerificationState v={a.verification} />
      {live && !hasSigned && <div className="text-xs text-muted-foreground">Upload the signed agreement to enable verification.</div>}
      {!["terminated", "superseded"].includes(a.status) && <UploadBox a={a} onDone={onChange} />}
      <div>
        {a.documents.map((d) => (
          <div key={d.id} className="text-xs flex items-center gap-3 border-t py-2" style={{ borderColor: "var(--hi-border)" }} data-testid={`document-row-${d.kind}`}>
            <span className="font-semibold w-36">{d.kind.replace(/_/g, " ")}</span><span className="truncate">{d.filename}{d.description && ` — ${d.description}`}</span>
            <span className="font-mono text-muted-foreground" title={d.sha256}>{d.sha256.slice(0, 16)}…</span>
            <span className="text-muted-foreground">{(d.size / 1024).toFixed(0)} KB · {fmtDateTime(d.created_at)} · {d.created_by}</span>
            <Button size="sm" variant="ghost" className="ml-auto h-7" onClick={() => downloadFile(`/dpa/documents/${d.id}`, d.filename)} data-testid={`document-download-${d.id}`}><Download className="h-3.5 w-3.5" /></Button>
          </div>
        ))}
        {!a.documents.length && <div className="text-xs text-muted-foreground">No documents yet. The issued PDF is stored here automatically when the agreement is issued.</div>}
      </div>
      {verifying && a.verification?.status === "verified" && <div className="text-xs text-amber-800">Already verified — confirming again replaces the current verification record (the old one stays in history).</div>}
      {verifying && <VerifyDialog a={a} open={verifying} setOpen={setVerifying} onDone={onChange} />}
      {rejecting && <RejectDialog a={a} open={rejecting} setOpen={setRejecting} onDone={onChange} />}
    </Card>
  );
};
